import { useState } from 'react';
import { ArrowRight, Search, Loader, TrendingUp, Award, Target } from 'lucide-react';
import { getCareerBridges } from '../../services/recommendations';

export default function PasserellesPanel() {
  const [loading, setLoading] = useState(false);
  const [bridges, setBridges] = useState([]);
  const [filters, setFilters] = useState({
    currentJob: '',
    currentSector: ''
  });
  const [error, setError] = useState(null);

  const handleGetBridges = async () => {
    setLoading(true);
    setError(null);
    try {
      const result = await getCareerBridges(
        filters.currentJob || null,
        filters.currentSector || null,
        null // userId = utilisateur connecté
      );
      setBridges(result.bridges || []);
    } catch (err) {
      setError(err.response?.data?.error || 'Erreur lors de la recherche de passerelles');
      console.error('Erreur passerelles:', err);
    } finally {
      setLoading(false);
    }
  };

  const getDifficultyColor = (difficulty) => {
    if (difficulty === 'facile') return 'bg-green-100 text-green-700';
    if (difficulty === 'moyen') return 'bg-orange-100 text-orange-700';
    return 'bg-red-100 text-red-700';
  };

  const getDifficultyIcon = (difficulty) => {
    if (difficulty === 'facile') return '✓';
    if (difficulty === 'moyen') return '◐';
    return '●';
  };

  return (
    <div className="bg-white rounded-xl shadow-sm border border-gray-200 p-6">
      <div className="flex items-center gap-3 mb-6">
        <div className="w-10 h-10 bg-emerald-100 rounded-lg flex items-center justify-center">
          <ArrowRight className="w-5 h-5 text-emerald-600" />
        </div>
        <div>
          <h2 className="text-xl font-bold text-gray-900">Passerelles Métiers</h2>
          <p className="text-sm text-gray-600">Suggestions de mobilité professionnelle</p>
        </div>
      </div>

      {/* Filtres */}
      <div className="grid md:grid-cols-2 gap-4 mb-6">
        <div>
          <label className="block text-sm font-medium text-gray-700 mb-2">Métier actuel</label>
          <input
            type="text"
            value={filters.currentJob}
            onChange={(e) => setFilters({ ...filters, currentJob: e.target.value })}
            placeholder="Ex: Développeur web"
            className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-emerald-500 focus:border-transparent"
          />
        </div>

        <div>
          <label className="block text-sm font-medium text-gray-700 mb-2">Secteur actuel</label>
          <input
            type="text"
            value={filters.currentSector}
            onChange={(e) => setFilters({ ...filters, currentSector: e.target.value })}
            placeholder="Ex: Informatique"
            className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-emerald-500 focus:border-transparent"
          />
        </div>
      </div>

      {/* Bouton */}
      <button
        onClick={handleGetBridges}
        disabled={loading}
        className="w-full bg-emerald-600 hover:bg-emerald-700 text-white font-medium py-3 px-4 rounded-lg flex items-center justify-center gap-2 transition-colors disabled:opacity-50"
      >
        {loading ? (
          <>
            <Loader className="w-5 h-5 animate-spin" />
            Recherche en cours...
          </>
        ) : (
          <>
            <Search className="w-5 h-5" />
            Trouver des passerelles
          </>
        )}
      </button>

      {/* Erreur */}
      {error && (
        <div className="mt-4 p-4 bg-red-50 border border-red-200 rounded-lg text-red-700 text-sm">
          {error}
        </div>
      )}

      {/* Résultats */}
      {bridges.length > 0 && (
        <div className="mt-6">
          <div className="flex items-center justify-between mb-4">
            <h3 className="font-semibold text-gray-900">
              {bridges.length} passerelle{bridges.length > 1 ? 's' : ''} trouvée{bridges.length > 1 ? 's' : ''}
            </h3>
            <div className="flex gap-2 text-xs">
              <span className="px-2 py-1 bg-green-100 text-green-700 rounded">Facile</span>
              <span className="px-2 py-1 bg-orange-100 text-orange-700 rounded">Moyen</span>
              <span className="px-2 py-1 bg-red-100 text-red-700 rounded">Difficile</span>
            </div>
          </div>

          <div className="space-y-3 max-h-[500px] overflow-y-auto">
            {bridges.map((bridge, index) => (
              <div
                key={index}
                className="border border-gray-200 rounded-lg p-4 hover:border-emerald-300 hover:shadow-sm transition-all"
              >
                <div className="flex items-start justify-between gap-4 mb-3">
                  <div className="flex-1">
                    <div className="flex items-center gap-2 mb-2">
                      <Target className="w-4 h-4 text-emerald-600" />
                      <h4 className="font-semibold text-gray-900">{bridge.targetJob}</h4>
                    </div>
                    <div className="flex flex-wrap gap-2 mb-2">
                      {bridge.targetSector && (
                        <span className="px-2 py-1 bg-gray-100 text-gray-700 text-xs rounded-full">
                          {bridge.targetSector}
                        </span>
                      )}
                      {bridge.level && (
                        <span className="px-2 py-1 bg-blue-100 text-blue-700 text-xs rounded-full">
                          {bridge.level}
                        </span>
                      )}
                      {bridge.certificationCode && (
                        <span className="px-2 py-1 bg-purple-100 text-purple-700 text-xs rounded-full">
                          {bridge.certificationCode}
                        </span>
                      )}
                    </div>
                  </div>

                  <div className="flex flex-col items-end gap-2">
                    <div className="text-2xl font-bold text-emerald-600">
                      {Math.round(bridge.bridgeScore * 100)}%
                    </div>
                    <span className={`px-3 py-1 rounded-full text-xs font-medium ${getDifficultyColor(bridge.difficulty)}`}>
                      {getDifficultyIcon(bridge.difficulty)} {bridge.difficulty.charAt(0).toUpperCase() + bridge.difficulty.slice(1)}
                    </span>
                  </div>
                </div>

                {/* Compétences partagées */}
                {bridge.sharedCompetences && bridge.sharedCompetences.length > 0 && (
                  <div className="mb-3">
                    <div className="text-xs text-gray-600 mb-1">Compétences partagées:</div>
                    <div className="flex flex-wrap gap-1">
                      {bridge.sharedCompetences.map((comp, i) => (
                        <span key={i} className="px-2 py-0.5 bg-green-50 text-green-700 text-xs rounded">
                          {comp}
                        </span>
                      ))}
                    </div>
                  </div>
                )}

                {/* Formation requise */}
                {bridge.requiredTraining && (
                  <div className="flex items-center gap-2 text-xs text-orange-600 bg-orange-50 px-3 py-2 rounded">
                    <Award className="w-4 h-4" />
                    <span>Formation complémentaire recommandée</span>
                  </div>
                )}
              </div>
            ))}
          </div>
        </div>
      )}

      {!loading && bridges.length === 0 && !error && (
        <div className="mt-6 text-center py-12 text-gray-500">
          <TrendingUp className="w-12 h-12 mx-auto mb-3 text-gray-400" />
          <p>Découvrez les passerelles métiers possibles</p>
        </div>
      )}
    </div>
  );
}